import React, { useState } from "react";
import "./revenue-chart.css";
import { Skeleton } from "components/skeleton";

/** One day of the revenue chart. `value` is that day's NET revenue in the selected token. */
export interface RevenueDatum {
  /** Short display date, e.g. "Jul 29". */
  label: string;
  value: number;
  /** Sat/Sun gets the warmer "weekend" gradient. */
  isWeekend?: boolean;
}

interface Props {
  title?: string;
  meta?: string;
  /** One entry per day, oldest → newest. */
  data?: RevenueDatum[];
  /** Unit for the tooltip amount. */
  unit?: string;
  /** Show a shimmer placeholder instead of bars while payments load. */
  loading?: boolean;
}

// Fixed pseudo-heights (% of plot) for the loading skeleton's bars.
const SK_BAR_HEIGHTS = [
  40, 55, 35, 70, 50, 85, 60, 45, 75, 30, 65, 50, 90, 55, 40, 70, 60, 35, 80,
  50, 45, 65, 55, 75,
];

// SVG viewBox is 600×200; baseline sits at y=190 so bars sink to the floor.
const CHART_WIDTH = 600;
const CHART_HEIGHT = 200;
const BASELINE_Y = 190;
const TOP_Y = 18;
const MAX_BAR_HEIGHT = BASELINE_Y - TOP_Y;
const MIN_BAR_HEIGHT = 3;
const BAR_WIDTH = 12;

/**
 * Decide which group ("weekday" / "weekend") has the higher per-day average.
 * Returns null when either group is empty so the legend stays neutral.
 */
function pickPeakGroup(data: RevenueDatum[]): "weekday" | "weekend" | null {
  let weekdaySum = 0,
    weekdayCount = 0;
  let weekendSum = 0,
    weekendCount = 0;
  for (const d of data) {
    if (d.isWeekend) {
      weekendSum += d.value;
      weekendCount += 1;
    } else {
      weekdaySum += d.value;
      weekdayCount += 1;
    }
  }
  if (weekdayCount === 0 || weekendCount === 0) return null;
  return weekendSum / weekendCount >= weekdaySum / weekdayCount
    ? "weekend"
    : "weekday";
}

function formatMoney(n: number): string {
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function RevenueChart({
  title = "Revenue · Last 30 days",
  meta,
  data = [],
  unit = "",
  loading = false,
}: Props) {
  const [hovered, setHovered] = useState<number | null>(null);

  const hasRevenue = data.some((d) => d.value > 0);
  // Scale to the tallest day; guard against an all-zero window.
  const max = Math.max(...data.map((d) => d.value), 0.0001);
  const slot = data.length > 0 ? CHART_WIDTH / data.length : CHART_WIDTH;
  const peakGroup = pickPeakGroup(data);

  const active = hovered != null ? data[hovered] : null;

  return (
    <section className="revenue-chart">
      <div className="revenue-chart__header">
        <h3 className="revenue-chart__title">{title}</h3>
        {meta && <span className="revenue-chart__meta">{meta}</span>}
      </div>

      {loading ? (
        <>
          <div className="revenue-chart__sk-bars">
            {SK_BAR_HEIGHTS.map((h, i) => (
              <Skeleton
                key={i}
                width={BAR_WIDTH}
                height={`${h}%`}
                borderRadius="3px 3px 0 0"
              />
            ))}
          </div>
          <div className="revenue-chart__legend">
            <Skeleton width={80} height={11} />
            <Skeleton width={80} height={11} />
          </div>
        </>
      ) : !hasRevenue ? (
        <div className="revenue-chart__empty">No revenue data.</div>
      ) : (
        <>
          <div className="revenue-chart__plot">
            <svg
              className="revenue-chart__svg"
              viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
              preserveAspectRatio="none"
              xmlns="http://www.w3.org/2000/svg"
              onMouseLeave={() => setHovered(null)}
            >
              <defs>
                <linearGradient id="rc-grad-weekday" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4a8bc4" />
                  <stop offset="100%" stopColor="#2d6a9f" />
                </linearGradient>
                <linearGradient id="rc-grad-weekend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#e29262" />
                  <stop offset="100%" stopColor="#c07a4a" />
                </linearGradient>
              </defs>

              {/* Grid lines */}
              <g stroke="rgba(255,255,255,0.04)" strokeWidth="1">
                <line x1="0" y1="40" x2={CHART_WIDTH} y2="40" />
                <line x1="0" y1="90" x2={CHART_WIDTH} y2="90" />
                <line x1="0" y1="140" x2={CHART_WIDTH} y2="140" />
                <line x1="0" y1={BASELINE_Y} x2={CHART_WIDTH} y2={BASELINE_Y} />
              </g>

              {/* Bars (color by weekday vs. weekend) */}
              <g>
                {data.map((d, i) => {
                  if (d.value <= 0) return null;
                  const height = Math.max(
                    MIN_BAR_HEIGHT,
                    (d.value / max) * MAX_BAR_HEIGHT,
                  );
                  return (
                    <rect
                      key={i}
                      x={slot * i + (slot - BAR_WIDTH) / 2}
                      y={BASELINE_Y - height}
                      width={BAR_WIDTH}
                      height={height}
                      rx="3"
                      fill={
                        d.isWeekend
                          ? "url(#rc-grad-weekend)"
                          : "url(#rc-grad-weekday)"
                      }
                      opacity={hovered == null || hovered === i ? 1 : 0.45}
                    />
                  );
                })}
              </g>

              {/* Full-height transparent hit areas so every day (even empty
                  ones) is hoverable. */}
              <g>
                {data.map((_, i) => (
                  <rect
                    key={`hit-${i}`}
                    x={slot * i}
                    y="0"
                    width={slot}
                    height={CHART_HEIGHT}
                    fill="transparent"
                    onMouseEnter={() => setHovered(i)}
                  />
                ))}
              </g>
            </svg>

            {active && (
              <div
                className="revenue-chart__tooltip"
                style={{
                  left: `${((slot * hovered! + slot / 2) / CHART_WIDTH) * 100}%`,
                }}
              >
                <span className="revenue-chart__tooltip-date">
                  {active.label}
                </span>
                <span className="revenue-chart__tooltip-value">
                  {formatMoney(active.value)} {unit}
                </span>
              </div>
            )}
          </div>

          <div className="revenue-chart__legend">
            <span className="revenue-chart__legend-item">
              <span className="revenue-chart__swatch revenue-chart__swatch--weekday" />
              Weekday{peakGroup === "weekday" && " (peak)"}
            </span>
            <span className="revenue-chart__legend-item">
              <span className="revenue-chart__swatch revenue-chart__swatch--weekend" />
              Weekend{peakGroup === "weekend" && " (peak)"}
            </span>
          </div>
        </>
      )}
    </section>
  );
}

export default RevenueChart;
